<template>
  <v-dialog v-model="innerValue" max-width="1280" persistent scrollable>
    <v-card>
      <v-toolbar dense flat>
        <v-toolbar-title class="font-weight-medium">Configurar Reporte OEE</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="resetToDefaults" :title="'Restablecer a valores por defecto'">
          <v-icon>mdi-restore</v-icon>
        </v-btn>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-row>
          <!-- Panel izquierdo: Controles -->
          <v-col cols="12" md="6">
            <v-tabs v-model="tab" background-color="transparent" grow>
              <v-tab> Básico </v-tab>
              <v-tab> Columnas </v-tab>
              <v-tab> Colores </v-tab>
              <v-tab> Orden </v-tab>
              <v-tab> Filtros </v-tab>
              <v-tab> Avanzado </v-tab>
              <v-tab> JSON </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
              <!-- BASICO -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="7">
                        <v-select :items="versionesOEE" item-text="label" item-value="value" v-model="config.versionOEE"
                          label="Versión de OEE" hint="Define la fórmula que se utilizará" persistent-hint dense
                          outlined />
                      </v-col>

                      <v-col cols="12" md="5">
                        <v-text-field v-model="config.nombrePreset" dense outlined label="Nombre del preset"
                          hint="Para guardar como JSON local / BD" persistent-hint />
                      </v-col>
                    </v-row>

                    <v-divider class="my-4" />

                    <h3 class="subtitle-1 font-weight-bold mb-2">Datos generales (mostrar / ocultar)
                    </h3>
                    <v-row>
                      <v-col v-for="m in generalMetrics" :key="'chk-' + m.key" cols="12" sm="6" class="py-1">
                        <v-checkbox v-model="config.metricsEnabled[m.key]" :label="m.label" dense hide-details />
                      </v-col>
                    </v-row>

                    <v-divider class="my-4" />

                    <h3 class="subtitle-1 font-weight-bold mb-2">Paleta de gráfica (fallback)</h3>
                    <v-row>
                      <v-col v-for="(c, i) in config.chartPalette" :key="'pal-' + i" cols="6" sm="4" class="py-1">
                        <div class="d-flex align-center">
                          <input type="color" class="mr-2"
                            style="width:40px;height:32px;border:none;background:transparent"
                            v-model="config.chartPalette[i]" />
                          <v-text-field dense outlined hide-details v-model="config.chartPalette[i]" />
                          <v-btn icon small class="ml-1" @click="removePaletteColor(i)"><v-icon
                              small>mdi-delete</v-icon></v-btn>
                        </div>
                      </v-col>
                      <v-col cols="12">
                        <v-btn small text color="primary" @click="addPaletteColor">
                          <v-icon left small>mdi-plus</v-icon>Agregar color
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- COLUMNAS -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <p class="caption grey--text mb-4">
                      Ajusta el tamaño (grid de 12 columnas) para cada dato general mostrado en la
                      tarjeta de resumen.
                    </p>
                    <v-row v-for="m in generalMetrics" :key="'cols-' + m.key" class="align-center mb-1">
                      <v-col cols="6" class="py-1">
                        <div class="font-weight-medium">{{ m.label }}</div>
                      </v-col>
                      <v-col cols="6" class="py-1">
                        <v-slider v-model="config.generalColumns[m.key]" :max="12" :min="2" step="1" ticks tick-size="2"
                          persistent-hint :hint="config.generalColumns[m.key] + ' / 12 cols'" dense />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- COLORES -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <p class="caption grey--text mb-2">
                      Define bandas de color por porcentaje para cada métrica. El color aplicado
                      depende del valor normalizado (0–100).
                    </p>
                    <v-expansion-panels multiple>
                      <v-expansion-panel v-for="m in colorizableMetrics" :key="'cb-' + m.key">
                        <v-expansion-panel-header>
                          <div class="d-flex align-center">
                            <span class="font-weight-medium mr-2">{{ m.label }}</span>
                            <v-chip x-small>{{ m.key }}</v-chip>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-simple-table dense>
                            <thead>
                              <tr>
                                <th class="text-left" style="width:90px">Mín %</th>
                                <th class="text-left" style="width:90px">Máx %</th>
                                <th class="text-left">Color</th>
                                <th class="text-left" style="width:120px">Etiqueta</th>
                                <th class="text-left" style="width:80px"></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(band, idx) in config.colorBands[m.key]" :key="m.key + '-' + idx">
                                <td>
                                  <v-text-field type="number" dense hide-details outlined v-model.number="band.min" />
                                </td>
                                <td>
                                  <v-text-field type="number" dense hide-details outlined v-model.number="band.max" />
                                </td>
                                <td>
                                  <div class="d-flex align-center">
                                    <input type="color" class="mr-2"
                                      style="width:40px;height:32px;border:none;background:transparent"
                                      v-model="band.color" />
                                    <v-text-field dense outlined hide-details v-model="band.color" />
                                  </div>
                                </td>
                                <td>
                                  <v-text-field dense outlined hide-details v-model="band.label" />
                                </td>
                                <td class="text-right">
                                  <v-btn icon small @click="moveBand(m.key, idx, -1)"><v-icon
                                      small>mdi-chevron-up</v-icon></v-btn>
                                  <v-btn icon small @click="moveBand(m.key, idx, +1)"><v-icon
                                      small>mdi-chevron-down</v-icon></v-btn>
                                  <v-btn icon small @click="removeBand(m.key, idx)"><v-icon
                                      small>mdi-delete</v-icon></v-btn>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                          <div class="mt-2">
                            <v-btn small text color="primary" @click="addBand(m.key)"><v-icon left
                                small>mdi-plus</v-icon>Agregar banda</v-btn>
                            <v-btn small text color="secondary" @click="resetBands(m.key)"><v-icon left
                                small>mdi-restore</v-icon>Restablecer bandas</v-btn>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- ORDEN -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="7">
                        <v-select :items="sortableMetrics" item-text="label" item-value="key"
                          v-model="config.sort.metric" label="Ordenar por" dense outlined />
                      </v-col>
                      <v-col cols="12" md="5">
                        <v-btn-toggle v-model="config.sort.direction" mandatory class="mt-4">
                          <v-btn value="desc"><v-icon left>mdi-sort-descending</v-icon>Desc</v-btn>
                          <v-btn value="asc"><v-icon left>mdi-sort-ascending</v-icon>Asc</v-btn>
                        </v-btn-toggle>
                      </v-col>
                    </v-row>

                    <v-divider class="my-4" />

                    <h3 class="subtitle-1 font-weight-bold mb-2">Orden y visibilidad de columnas en
                      tabla</h3>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th>Columna</th>
                          <th class="text-center" style="width:80px">Mostrar</th>
                          <th class="text-right" style="width:160px">Mover</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(col, idx) in config.table.columns" :key="col.key">
                          <td>{{ col.label }}</td>
                          <td class="text-center">
                            <v-switch v-model="col.visible" inset hide-details />
                          </td>
                          <td class="text-right">
                            <v-btn icon small @click="moveColumn(idx, -1)"><v-icon small>mdi-chevron-up</v-icon></v-btn>
                            <v-btn icon small @click="moveColumn(idx, +1)"><v-icon
                                small>mdi-chevron-down</v-icon></v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- FILTROS -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="filters.maquinaQuery" label="Filtrar por nombre de máquina"
                          prepend-inner-icon="mdi-magnify" dense outlined />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field type="number" min="1" v-model.number="config.filters.topN"
                          label="Top N (opcional)" hint="Limita la vista previa" persistent-hint dense outlined />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-switch v-model="config.filters.soloConProduccion" label="Solo máquinas con producción" inset
                          hide-details />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select v-model="config.filters.estatus"
                          :items="[{ text: 'Todos', value: null }, { text: 'Activas', value: 'activa' }, { text: 'Inactivas', value: 'inactiva' }]"
                          label="Estatus (ejemplo)" dense outlined />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- AVANZADO -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-select :items="['auto', '0-1', '0-100']" v-model="config.formatoPorcentaje"
                          label="Formato de %" dense outlined hint="'auto' detecta si es 0–1 o 0–100" persistent-hint />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field type="number" min="0" max="6" v-model.number="config.decimales" label="Decimales"
                          dense outlined />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-switch v-model="config.table.showTotalsRow" label="Mostrar fila de totales" inset
                          hide-details />
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <v-alert type="info" dense text outlined>
                          <div>
                            <strong>Clave localStorage:</strong> {{ localKey }}.
                            Usa los botones de guardar para persistir / emitir a tu backend.
                          </div>
                        </v-alert>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- JSON -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-textarea v-model="jsonPreview" label="JSON de configuración (solo lectura)" rows="16" outlined
                      readonly class="font-mono" />
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </v-col>

          <!-- Panel derecho: Vista previa -->
          <v-col cols="12" md="6">
            <v-card outlined>
              <v-card-title class="py-2">
                Vista previa
                <v-spacer />
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" @click="recomputePreview">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>Recalcular con la configuración actual</span>
                </v-tooltip>
              </v-card-title>
              <v-divider />

              <!-- Tarjetas de resumen -->
              <v-card-text>
                <v-row dense>
                  <template v-for="m in generalMetrics">
                    <v-col v-if="config.metricsEnabled[m.key]" :key="'sum-' + m.key"
                      :cols="Math.min(12, Math.max(2, config.generalColumns[m.key]))" class="pb-2">
                      <v-sheet :elevation="1" class="pa-3 rounded-lg d-flex align-center">
                        <div class="mr-3"
                          :style="{ width: '10px', height: '36px', backgroundColor: getMetricColor(m.key, resumenPct[m.key]) }">
                        </div>
                        <div class="flex-grow-1">
                          <div class="caption grey--text text--darken-1">{{ m.label }}</div>
                          <div class="title font-weight-bold">{{ formatPct(resumenPct[m.key])
                          }}</div>
                        </div>
                      </v-sheet>
                    </v-col>
                  </template>
                </v-row>

                <!-- Mini gráfico de barras (sin librerías) -->
                <!-- OEE por máquina (ApexCharts) -->
                <div class="mt-4">
                  <div class="subtitle-2 font-weight-bold mb-2">OEE por máquina</div>
                  <apexchart type="bar" height="260" :options="apxOeeOptions" :series="apxOeeSeries" />
                </div>


                <!-- Tabla -->
                <div class="mt-4">
                  <v-simple-table dense>
                    <thead>
                      <tr>
                        <th v-for="col in visibleColumns" :key="'th-' + col.key" class="text-left">
                          {{ col.label }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in previewRows" :key="'r-' + i">
                        <td v-for="col in visibleColumns" :key="'td-' + col.key">
                          <template v-if="col.key.endsWith('Pct')">
                            {{ formatPct(row[col.key]) }}
                          </template>
                          <template v-else>
                            {{ row[col.key] }}
                          </template>
                        </td>
                      </tr>
                      <tr v-if="config.table.showTotalsRow && previewRows.length">
                        <td v-for="col in visibleColumns" :key="'tot-' + col.key">
                          <template v-if="col.aggregate === 'sum'">{{sum(previewRows.map(r =>
                            r[col.key]))}}</template>
                          <template v-else-if="col.aggregate === 'avg'">{{
                            formatPct(avg(previewRows.map(r => r[col.key])))}}</template>
                          <template v-else>—</template>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn text @click="close">Cancelar</v-btn>
        <v-spacer />
        <v-btn color="secondary" text @click="saveToLocal"><v-icon left>mdi-content-save</v-icon>Guardar en JSON
          local</v-btn>
        <v-btn color="primary" dark @click="emitSaveToDB"><v-icon left>mdi-database</v-icon>Guardar en
          BD</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * DialogConfigOEE.vue (Vuetify 2, Vue 2 - Options API)
 * - Configura versión OEE, tamaños de columnas, bandas de color por %, orden/filtros y vista previa.
 * - Persiste en localStorage y emite JSON para guardar en BD.
 *
 * Props:
 *  - value: Boolean (v-model para abrir/cerrar)
 *  - dataJson: Object (JSON con campos: resumen, detallesMaquinas, ...)
 *  - initialConfig: Object opcional para iniciar el preset
 *  - localKey: String clave para localStorage (default: 'oee_config_v1')
 *
 * Eventos:
 *  - input(Boolean)        -> v-model
 *  - guardar-db(Object)    -> enviar al backend
 *  - snackbar(String)      -> mensaje rápido opcional
 */
export default {
  props: {
    value: { type: Boolean, default: false },
    dataJson: { type: Object, required: true },
    initialConfig: { type: Object, default: null },
    localKey: { type: String, default: 'oee_config_v1' }
  },
  data() {
    return {
      tab: 0,
      innerValue: this.value,
      config: this.makeDefaultConfig(),
      filters: {
        maquinaQuery: ''
      },
      versionesOEE: [
        { value: 'v3', label: 'v3 (estándar clásico)' },
        { value: 'v4', label: 'v4' },
        { value: 'v5', label: 'v5' },
        { value: 'v6', label: 'v6' },
        { value: 'v7', label: 'v7' },
        { value: 'v8', label: 'v8 (actual)' }
      ]
    }
  },
  computed: {
    generalMetrics() {
      return [
        { key: 'disponibilidad', label: 'Disponibilidad' },
        { key: 'eficiencia', label: 'Eficiencia' },
        { key: 'calidad', label: 'Calidad' },
        { key: 'oee', label: 'OEE' }
      ]
    },
    colorizableMetrics() {
      return [
        { key: 'oee', label: 'OEE' },
        { key: 'disponibilidad', label: 'Disponibilidad' },
        { key: 'eficiencia', label: 'Eficiencia' },
        { key: 'calidad', label: 'Calidad' }
      ]
    },
    sortableMetrics() {
      return [
        { key: '_oeePct', label: 'OEE' },
        { key: '_dispPct', label: 'Disponibilidad' },
        { key: '_eficPct', label: 'Eficiencia' },
        { key: '_calPct', label: 'Calidad' },
        { key: 'totalPiezas', label: 'Piezas' },
        { key: 'piezasOk', label: 'Piezas OK' },
        { key: 'piezasNoOk', label: 'Piezas NOK' }
      ]
    },
    jsonPreview() {
      return JSON.stringify(this.config, null, 2)
    },
    resumenPct() {
      const r = this.dataJson?.resumen || {}
      return {
        disponibilidad: this.normalizePct(r.disponibilidad),
        eficiencia: this.normalizePct(r.eficiencia),
        calidad: this.normalizePct(r.calidad),
        oee: this.normalizePct(r.oee)
      }
    },
    rowsBase() {
      const arr = Array.isArray(this.dataJson?.detallesMaquinas) ? this.dataJson.detallesMaquinas : []
      return arr.map(x => ({
        nombre: x.nombre || x.maquina || ('M' + (x.idMaquinaIns || x.idMaquina || '?')),
        totalPiezas: x.totalPiezas ?? 0,
        piezasOk: x.piezasOk ?? 0,
        piezasNoOk: x.piezasNoOk ?? 0,
        _dispPct: this.normalizePct(x.disponibilidad),
        _eficPct: this.normalizePct(x.eficiencia),
        _calPct: this.normalizePct(x.calidad),
        _oeePct: this.normalizePct(x.oee)
      }))
    },
    previewRows() {
      let rows = this.rowsBase
      if (this.config.filters.soloConProduccion) {
        rows = rows.filter(r => (r.totalPiezas || 0) > 0)
      }
      if (this.filters.maquinaQuery) {
        const q = this.filters.maquinaQuery.toLowerCase()
        rows = rows.filter(r => (r.nombre || '').toLowerCase().includes(q))
      }
      const metric = this.config.sort.metric || '_oeePct'
      const dir = this.config.sort.direction === 'asc' ? +1 : -1
      rows = rows.slice().sort((a, b) => (a[metric] - b[metric]) * dir)
      if (this.config.filters.topN && this.config.filters.topN > 0) {
        rows = rows.slice(0, this.config.filters.topN)
      }
      return rows
    },
    visibleColumns() {
      return this.config.table.columns.filter(c => c.visible)
    },

    apxCategories() {
      return this.previewRows.map(r => r.nombre)
    },
    apxValues() {
      // OEE ya normalizado a 0–100
      return this.previewRows.map(r => Number((r._oeePct || 0).toFixed(2)))
    },
    apxColors() {
      // Color por barra según bandas de color configuradas
      return this.previewRows.map(r => this.getMetricColor('oee', r._oeePct) || '#9E9E9E')
    },
    apxOeeSeries() {
      return [{ name: 'OEE', data: this.apxValues }]
    },
    apxOeeOptions() {
      const dec = Math.max(0, Math.min(6, this.config.decimales || 0))
      return {
        chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: true } },
        plotOptions: {
          bar: {
            distributed: true,           // un color por barra (usa apxColors)
            columnWidth: '55%',
            dataLabels: { position: 'top' }
          }
        },
        colors: this.apxColors,          // colores por barra
        dataLabels: {
          enabled: true,
          formatter: (val) => this.formatPct(val), // respeta tus decimales
          offsetY: -14,
          style: { fontSize: '11px' }
        },
        tooltip: {
          y: { formatter: (val) => this.formatPct(val) }
        },
        xaxis: {
          categories: this.apxCategories,
          labels: { rotate: -35, trim: true }
        },
        yaxis: {
          max: 100,
          labels: {
            formatter: (val) => `${val.toFixed(dec)}%`
          }
        },
        grid: { strokeDashArray: 4 },
        legend: { show: false },
        theme: { mode: 'light' } // o 'dark' si usas tema oscuro
      }
    },
  },
  watch: {
    value(val) {
      this.innerValue = val
      if (val) this.tryInitConfig()
    },
    innerValue(val) { this.$emit('input', val) }
  },
  created() {
    this.tryInitConfig()
  },
  methods: {
    // ---- INIT & DEFAULTS ----
    tryInitConfig() {
      const local = this.loadFromLocal()
      if (this.initialConfig) this.config = this.mergeDeep(this.makeDefaultConfig(), this.initialConfig)
      else if (local) this.config = this.mergeDeep(this.makeDefaultConfig(), local)
      else this.config = this.makeDefaultConfig()
    },
    makeDefaultConfig() {
      return {
        nombrePreset: 'Preset OEE',
        versionOEE: 'v8',
        formatoPorcentaje: 'auto', // 'auto' | '0-1' | '0-100'
        decimales: 2,
        chartPalette: ['#E53935', '#FB8C00', '#FDD835', '#43A047', '#1E88E5', '#8E24AA'],
        metricsEnabled: {
          disponibilidad: true,
          eficiencia: true,
          calidad: true,
          oee: true
        },
        generalColumns: {
          disponibilidad: 3,
          eficiencia: 3,
          calidad: 3,
          oee: 3
        },
        colorBands: {
          oee: [{ min: 0, max: 80, color: '#E53935', label: 'Bajo' },
          { min: 80, max: 95, color: '#FDD835', label: 'Medio' },
          { min: 95, max: 101, color: '#43A047', label: 'Alto' }],
          disponibilidad: [{ min: 0, max: 85, color: '#E53935', label: 'Bajo' },
          { min: 85, max: 95, color: '#FDD835', label: 'Medio' },
          { min: 95, max: 101, color: '#43A047', label: 'Alto' }],
          eficiencia: [{ min: 0, max: 85, color: '#E53935', label: 'Bajo' },
          { min: 85, max: 95, color: '#FDD835', label: 'Medio' },
          { min: 95, max: 101, color: '#43A047', label: 'Alto' }],
          calidad: [{ min: 0, max: 99, color: '#FDD835', label: 'Medio' },
          { min: 99, max: 101, color: '#43A047', label: 'Alto' }]
        },
        sort: { metric: '_oeePct', direction: 'desc' },
        filters: {
          topN: null,
          soloConProduccion: false,
          estatus: null
        },
        table: {
          dense: true,
          fixedHeader: true,   // informativo para tu uso con v-data-table si lo prefieres
          showTotalsRow: true,
          columns: [
            { key: 'nombre', label: 'Máquina', visible: true },
            { key: '_dispPct', label: 'Disp %', visible: true, aggregate: 'avg' },
            { key: '_eficPct', label: 'Efic %', visible: true, aggregate: 'avg' },
            { key: '_calPct', label: 'Cal %', visible: true, aggregate: 'avg' },
            { key: '_oeePct', label: 'OEE %', visible: true, aggregate: 'avg' },
            { key: 'totalPiezas', label: 'Piezas', visible: true, aggregate: 'sum' },
            { key: 'piezasOk', label: 'OK', visible: false, aggregate: 'sum' },
            { key: 'piezasNoOk', label: 'NOK', visible: false, aggregate: 'sum' }
          ]
        }
      }
    },

    // ---- HELPERS ----
    close() { this.innerValue = false },
    resetToDefaults() { this.config = this.makeDefaultConfig() },
    saveToLocal() {
      localStorage.setItem(this.localKey, JSON.stringify(this.config))
      this.$emit('snackbar', 'Preset guardado en JSON local')
    },
    loadFromLocal() {
      try {
        const raw = localStorage.getItem(this.localKey)
        return raw ? JSON.parse(raw) : null
      } catch (e) { return null }
    },
    emitSaveToDB() {
      this.$emit('guardar-db', JSON.parse(JSON.stringify(this.config)))
    },
    recomputePreview() {
      // Fuerza recomputado (clon superficial)
      this.config = this.mergeDeep({}, this.config)
    },

    // ---- BANDS & COLORS ----
    addPaletteColor() { this.config.chartPalette.push('#607D8B') },
    removePaletteColor(i) { this.config.chartPalette.splice(i, 1) },
    addBand(metricKey) {
      if (!Array.isArray(this.config.colorBands[metricKey])) this.$set(this.config.colorBands, metricKey, [])
      this.config.colorBands[metricKey].push({ min: 0, max: 100, color: '#90A4AE', label: '' })
    },
    removeBand(metricKey, idx) {
      this.config.colorBands[metricKey].splice(idx, 1)
    },
    moveBand(metricKey, idx, dir) {
      const arr = this.config.colorBands[metricKey]
      const j = idx + dir
      if (j < 0 || j >= arr.length) return
      const x = arr[idx]; this.$set(arr, idx, arr[j]); this.$set(arr, j, x)
    },
    resetBands(metricKey) {
      const def = this.makeDefaultConfig().colorBands[metricKey]
      this.$set(this.config.colorBands, metricKey, JSON.parse(JSON.stringify(def)))
    },
    getMetricColor(metricKey, pct) {
      const val = typeof pct === 'number' ? pct : 0
      const bands = this.config.colorBands[metricKey] || []
      for (const b of bands) {
        if (val >= (b.min ?? 0) && val < (b.max ?? 100)) return b.color || '#9E9E9E'
      }
      // fallback paleta
      return this.config.chartPalette[0] || '#9E9E9E'
    },

    // ---- COLUMNS ORDER ----
    moveColumn(idx, dir) {
      const cols = this.config.table.columns
      const j = idx + dir
      if (j < 0 || j >= cols.length) return
      const x = cols[idx]; this.$set(cols, idx, cols[j]); this.$set(cols, j, x)
    },

    // ---- NUMERIC ----
    normalizePct(v) {
      if (v == null || isNaN(v)) return 0
      if (this.config.formatoPorcentaje === '0-1') return Math.max(0, Math.min(v * 100, 100))
      if (this.config.formatoPorcentaje === '0-100') return Math.max(0, Math.min(v, 100))
      // auto
      if (v <= 1) return Math.max(0, Math.min(v * 100, 100))
      if (v > 100) return Math.max(0, Math.min(v / 100, 100))
      return Math.max(0, Math.min(v, 100))
    },
    formatPct(v) {
      const dec = Math.max(0, Math.min(6, this.config.decimales || 0))
      const n = (v ?? 0).toFixed(dec)
      return `${n}%`
    },
    sum(arr) {
      const n = (arr || []).reduce((a, b) => a + (parseFloat(b) || 0), 0)
      return Number.isFinite(n) ? n : 0
    },
    avg(arr) {
      if (!arr || !arr.length) return 0
      return (this.sum(arr) / arr.length) || 0
    },

    // ---- UTILS ----
    mergeDeep(target, source) {
      if (Array.isArray(source)) return source.map(x => this.mergeDeep({}, x))
      if (source && typeof source === 'object') {
        const out = Object.assign({}, target)
        Object.keys(source).forEach(k => {
          out[k] = this.mergeDeep(out[k], source[k])
        })
        return out
      }
      return source === undefined ? target : source
    }
  }
}
</script>

<style scoped>
.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.rounded-t {
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}
</style>
